import { useState } from 'react';
import { Modal, Input, Textarea, useToast } from '@/shared/components/ui';
import { Save, X } from 'lucide-react';
import { rolService } from '../api/rolService';
import { CrearRolDto } from '@/core/sdk/modules/administracion/rolClient';
import { LIMITES_VALIDACION, MENSAJES_ERROR } from '../constants';
import styles from './RolModals.module.css';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreateRolModal = ({ isOpen, onClose, onSuccess }: Props) => {
    const [formData, setFormData] = useState<CrearRolDto>({
        nombre: '',
        descripcion: ''
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const { showToast } = useToast();

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.nombre.trim()) {
            newErrors.nombre = MENSAJES_ERROR.NOMBRE_REQUERIDO;
        } else if (formData.nombre.length > LIMITES_VALIDACION.NOMBRE_MAX) {
            newErrors.nombre = MENSAJES_ERROR.NOMBRE_EXCEDE_LIMITE;
        }

        if (formData.descripcion && formData.descripcion.length > LIMITES_VALIDACION.DESCRIPCION_MAX) {
            newErrors.descripcion = MENSAJES_ERROR.DESCRIPCION_EXCEDE_LIMITE;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);

            const dataToSend = {
                ...formData,
                descripcion: formData.descripcion?.trim() || undefined
            };

            await rolService.create(dataToSend);

            showToast({
                type: 'success',
                message: MENSAJES_ERROR.CREADO_EXITOSO
            });

            onSuccess();
        } catch (error: any) {
            const errorMessage = error.response?.data?.message ||
                error.message ||
                MENSAJES_ERROR.ERROR_CREAR;

            showToast({
                type: 'error',
                message: errorMessage
            });

            console.error('[CreateRol] Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field: keyof CrearRolDto, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Limpiar error del campo al escribir
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Nuevo Rol"
            size="lg"
            closeOnOverlayClick={false}
            headerActions={
                <>
                    <button
                        type="button"
                        className={`${styles.iconBtn} ${styles.saveButton}`}
                        onClick={handleSubmit}
                        disabled={loading}
                        title="Guardar"
                        aria-label="Guardar nuevo rol"
                    >
                        <Save size={18} strokeWidth={2.5} />
                    </button>
                    <button
                        type="button"
                        className={`${styles.iconBtn} ${styles.cancelButton}`}
                        onClick={onClose}
                        disabled={loading}
                        title="Cancelar"
                        aria-label="Cancelar creación"
                    >
                        <X size={18} strokeWidth={2.5} />
                    </button>
                </>
            }
        >
            <form onSubmit={handleSubmit} className={styles.form}>
                <Input
                    label="Nombre *"
                    value={formData.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    error={errors.nombre}
                    placeholder="Ej: Administrador, Supervisor, Operador"
                    disabled={loading}
                />

                <Textarea
                    label="Descripción"
                    value={formData.descripcion || ''}
                    onChange={(e) => handleChange('descripcion', e.target.value)}
                    error={errors.descripcion}
                    placeholder="Descripción opcional del rol"
                    fullWidth
                    disabled={loading}
                    resize="vertical"
                />
            </form>
        </Modal>
    );
};
